// Export comptable : le journal des ventes, dans le format que lit le
// logiciel du comptable.
//
// Le modèle est l'export qu'Extrabat produit et que le cabinet importe déjà :
// une facture devient plusieurs LIGNES D'ÉCRITURE — une par article (compte de
// ventes, au crédit), une par taux de TVA (compte de TVA collectée, au crédit)
// et une pour le client (compte client, au débit, pour le TTC). Un avoir est
// la même chose, sens inversés.
//
//   20/07/2026;VE;7061;FAC00000628   DEPETRIS ROBERT;;250,00;;FAC00000628;ARTICLE;;;;
//   20/07/2026;VE;445710;FAC00000628   DEPETRIS ROBERT;;142,76;;FAC00000628;TVA;;;;
//   20/07/2026;VE;411000;FAC00000628   DEPETRIS ROBERT;1570,40;;;FAC00000628;CLIENT;;;;
//
// Deux règles tiennent ce module :
//   1. Chaque pièce est ÉQUILIBRÉE : la somme des débits égale celle des
//      crédits, au centime. Une écriture déséquilibrée est refusée par le
//      logiciel comptable, et c'est la première chose que le comptable vérifie.
//      Les arrondis de lignes sont absorbés sur la dernière ligne d'article,
//      et signalés.
//   2. Rien n'est inventé : les montants viennent de la facture (ses lignes,
//      son détail de TVA, son TTC), les numéros de compte des réglages posés
//      avec le comptable. Un compte absent du plan n'est pas remplacé en
//      silence — il est signalé à l'écran, au comptable de trancher.
//
// Fonctions PURES : pas d'accès réseau, pas d'horloge, tout est paramètre.

/** Une ligne de facture, réduite à ce qui sert à l'écriture. */
export interface LigneFactureExport {
  libelle: string;
  totalHt: number;
  /** Taux de TVA de la ligne en pourcentage (10 pour 10 %) ; null si inconnu. */
  tauxTva: number | null;
  /** Nature de l'article (materiau, prestation, main_oeuvre…), pour un compte de ventes dédié. */
  kind: string | null;
}

export interface FactureExport {
  numero: string;
  /** Compte client individuel (C00012), null si le client n'en a pas encore. */
  compteClient?: string | null;
  /** Date d'émission, yyyy-mm-dd. */
  dateEmission: string;
  /** Nom du client tel qu'il doit apparaître dans le libellé. */
  client: string;
  /** standard | acompte | situation | finale | avoir */
  kind: string;
  lignes: LigneFactureExport[];
  /** Détail de TVA par taux, tel que la facture l'a figé à l'émission. */
  detailTva: { taux: number; baseHt: number; montantTva: number }[];
  montantHt: number;
  montantTva: number;
  montantTtc: number;
}

/** Les comptes à utiliser — posés avec le comptable, jamais devinés. */
export interface ComptesExport {
  /** Code du journal des ventes (VE). */
  journal: string;
  /** Compte de ventes par défaut (7061 chez Extrabat). */
  ventes: string;
  /** Comptes de ventes par nature d'article, quand le comptable les distingue. */
  ventesParKind: Record<string, string>;
  /** Compte client collectif (411000). */
  client: string;
  /** Compte de TVA collectée par taux ('10' -> '445710'). */
  tvaParTaux: Record<string, string>;
  /** Compte de TVA collectée pour un taux non listé. */
  tvaDefaut: string;
  /** Un compte par client (C + numéro) plutôt que le collectif 411000. */
  clientsIndividuels: boolean;
  /** Préfixe des comptes clients individuels. */
  prefixeClient: string;
  /** Nombre de chiffres après le préfixe (C00012 = 5). */
  chiffresClient: number;
  /** Compte de trésorerie des règlements : banque (512000) et caisse (531000). */
  banque: string;
  caisse: string;
  /** Codes des journaux de règlement. */
  journalBanque: string;
  journalCaisse: string;
}

/** Ce qu'Extrabat utilise dans le fichier remis par le comptable. */
export const COMPTES_PAR_DEFAUT: ComptesExport = {
  journal: 'VE',
  ventes: '7061',
  ventesParKind: {},
  client: '411000',
  tvaParTaux: { '10': '445710' },
  tvaDefaut: '445710',
  clientsIndividuels: true,
  prefixeClient: 'C',
  chiffresClient: 5,
  banque: '512000',
  caisse: '531000',
  journalBanque: 'BQ',
  journalCaisse: 'CA',
};

export type TypeEcriture = 'ARTICLE' | 'TVA' | 'CLIENT' | 'BANQUE' | 'CAISSE';

export interface EcritureComptable {
  /** dd/mm/yyyy */
  date: string;
  journal: string;
  compte: string;
  libelle: string;
  debit: number | null;
  credit: number | null;
  piece: string;
  type: TypeEcriture;
}

export interface AvertissementExport {
  piece: string;
  message: string;
}

export interface ResultatExport {
  ecritures: EcritureComptable[];
  avertissements: AvertissementExport[];
  /** Nombre de pièces (factures et avoirs) exportées. */
  nbPieces: number;
  totalHt: number;
  totalTva: number;
  totalTtc: number;
}

const arrondi = (n: number): number => Math.round(n * 100) / 100;

/** Clé d'un taux dans `tvaParTaux` : « 10 », « 5.5 », « 20 » — sans zéro inutile. */
export function cleTaux(taux: number): string {
  return String(arrondi(taux));
}

/** yyyy-mm-dd -> dd/mm/yyyy, sans passer par Date (aucun décalage horaire possible). */
export function dateFrancaise(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3] ?? ''}/${m[2] ?? ''}/${m[1] ?? ''}` : iso;
}

/**
 * Le libellé d'écriture : numéro, trois espaces, client — comme dans le
 * modèle. Les points-virgules et retours à la ligne en sont retirés : le
 * format n'a pas de guillemets, un « ; » dans un nom décalerait la colonne.
 */
export function libelleEcriture(numero: string, client: string): string {
  const propre = client.replace(/[;\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
  return `${numero}   ${propre}`;
}

function compteVentes(comptes: ComptesExport, kind: string | null): string {
  if (kind && comptes.ventesParKind[kind]) return comptes.ventesParKind[kind] ?? comptes.ventes;
  return comptes.ventes;
}

/** Le compte du client : le sien s'il en a un et si le comptable en veut, sinon le collectif. */
export function compteClient(comptes: ComptesExport, code: string | null): string {
  const c = code?.trim() ?? '';
  return comptes.clientsIndividuels && c !== '' ? c : comptes.client;
}

/**
 * Le prochain code client libre : le plus grand numéro déjà attribué, plus un.
 * Un code n'est jamais réattribué, même si son client disparaît — le comptable
 * a pu l'utiliser.
 */
export function codeClientSuivant(existants: Iterable<string>, prefixe: string, chiffres: number): string {
  let max = 0;
  const p = prefixe.toUpperCase();
  for (const c of existants) {
    const code = c.trim().toUpperCase();
    if (!code.startsWith(p)) continue;
    const n = Number(code.slice(p.length));
    if (Number.isInteger(n) && n > max) max = n;
  }
  return `${p}${String(max + 1).padStart(chiffres, '0')}`;
}

function compteTva(comptes: ComptesExport, taux: number): string {
  return comptes.tvaParTaux[cleTaux(taux)] ?? comptes.tvaDefaut;
}

/**
 * Les écritures d'UNE pièce, équilibrées.
 *
 * Le HT de référence est la somme des bases du détail de TVA (c'est ce que la
 * facture a figé à l'émission) ; si les lignes ne tombent pas juste au centime
 * près, la différence est portée sur la dernière ligne d'article et signalée.
 * Le TTC écrit au client est HT + TVA, pour que la pièce s'équilibre TOUJOURS ;
 * s'il s'écarte du TTC de la facture, c'est signalé aussi.
 */
export function ecrituresFacture(
  f: FactureExport,
  comptes: ComptesExport,
): { ecritures: EcritureComptable[]; avertissements: AvertissementExport[] } {
  const avertissements: AvertissementExport[] = [];
  const date = dateFrancaise(f.dateEmission);
  const libelle = libelleEcriture(f.numero, f.client);
  const avoir = f.kind === 'avoir';
  const base = { date, journal: comptes.journal, libelle, piece: f.numero };
  // Sur un avoir, tout change de côté : produit et TVA au débit, client au crédit.
  const poser = (compte: string, type: TypeEcriture, montant: number, sensNormal: 'debit' | 'credit'): EcritureComptable => {
    const m = arrondi(Math.abs(montant));
    const auDebit = (sensNormal === 'debit') !== avoir;
    return { ...base, compte, type, debit: auDebit ? m : null, credit: auDebit ? null : m };
  };

  // Lignes d'article, regroupées par compte de ventes pour ne pas multiplier
  // les écritures quand le comptable ne distingue pas les natures.
  const lignes = f.lignes.filter((l) => arrondi(l.totalHt) !== 0);
  const htLignes = arrondi(lignes.reduce((s, l) => s + l.totalHt, 0));
  const htDetail = arrondi(f.detailTva.reduce((s, d) => s + d.baseHt, 0));
  const htReference = f.detailTva.length > 0 ? htDetail : arrondi(f.montantHt);
  const ecart = arrondi(htReference - htLignes);

  const articles: EcritureComptable[] = lignes.map((l) =>
    poser(compteVentes(comptes, l.kind), 'ARTICLE', l.totalHt, 'credit'),
  );
  if (articles.length === 0 && htReference !== 0) {
    // Pas de lignes (facture d'acompte en pourcentage, par exemple) : une seule écriture de produit.
    articles.push(poser(comptes.ventes, 'ARTICLE', htReference, 'credit'));
  } else if (ecart !== 0 && articles.length > 0) {
    const derniere = articles[articles.length - 1];
    if (derniere) {
      const montant = arrondi((derniere.debit ?? derniere.credit ?? 0) + ecart);
      if (derniere.debit !== null) derniere.debit = montant;
      else derniere.credit = montant;
      avertissements.push({
        piece: f.numero,
        message: `lignes d'article (${htLignes.toFixed(2)}) ≠ base HT de la facture (${htReference.toFixed(2)}) : écart de ${ecart.toFixed(2)} porté sur la dernière ligne`,
      });
    }
  }

  // TVA par taux, depuis le détail figé de la facture.
  const tva: EcritureComptable[] = f.detailTva
    .filter((d) => arrondi(d.montantTva) !== 0)
    .map((d) => poser(compteTva(comptes, d.taux), 'TVA', d.montantTva, 'credit'));
  const totalTva = arrondi(tva.reduce((s, e) => s + (e.debit ?? e.credit ?? 0), 0));
  if (arrondi(f.montantTva) !== totalTva) {
    avertissements.push({
      piece: f.numero,
      message: `TVA du détail (${totalTva.toFixed(2)}) ≠ TVA de la facture (${arrondi(f.montantTva).toFixed(2)})`,
    });
  }

  // Client : HT + TVA, pour que la pièce s'équilibre quoi qu'il arrive.
  const totalProduit = arrondi(articles.reduce((s, e) => s + (e.debit ?? e.credit ?? 0), 0));
  const ttc = arrondi(totalProduit + totalTva);
  if (arrondi(Math.abs(f.montantTtc)) !== ttc) {
    avertissements.push({
      piece: f.numero,
      message: `HT + TVA (${ttc.toFixed(2)}) ≠ TTC de la facture (${arrondi(Math.abs(f.montantTtc)).toFixed(2)})`,
    });
  }
  const client = poser(compteClient(comptes, f.compteClient ?? null), 'CLIENT', ttc, 'debit');

  return { ecritures: [...articles, ...tva, client], avertissements };
}

/** Le journal complet d'une liste de pièces, dans l'ordre reçu. */
export function journalVentes(factures: FactureExport[], comptes: ComptesExport): ResultatExport {
  const ecritures: EcritureComptable[] = [];
  const avertissements: AvertissementExport[] = [];
  let totalHt = 0;
  let totalTva = 0;
  let totalTtc = 0;
  for (const f of factures) {
    const r = ecrituresFacture(f, comptes);
    ecritures.push(...r.ecritures);
    avertissements.push(...r.avertissements);
    const signe = f.kind === 'avoir' ? -1 : 1;
    for (const e of r.ecritures) {
      const m = e.debit ?? e.credit ?? 0;
      if (e.type === 'ARTICLE') totalHt += signe * m;
      else if (e.type === 'TVA') totalTva += signe * m;
      else totalTtc += signe * m;
    }
  }
  return {
    ecritures,
    avertissements,
    nbPieces: factures.length,
    totalHt: arrondi(totalHt),
    totalTva: arrondi(totalTva),
    totalTtc: arrondi(totalTtc),
  };
}

/** Débit total, crédit total et écart : l'écart DOIT être nul. */
export function equilibre(ecritures: EcritureComptable[]): { debit: number; credit: number; ecart: number } {
  const debit = arrondi(ecritures.reduce((s, e) => s + (e.debit ?? 0), 0));
  const credit = arrondi(ecritures.reduce((s, e) => s + (e.credit ?? 0), 0));
  return { debit, credit, ecart: arrondi(debit - credit) };
}

/** 1570.4 -> « 1570,40 » : virgule décimale, deux décimales, pas de séparateur de milliers. */
export function montantCsv(n: number | null): string {
  return n === null ? '' : n.toFixed(2).replace('.', ',');
}

/**
 * Le fichier : une ligne par écriture, treize champs séparés par « ; », les
 * quatre derniers vides, retour à la ligne LF — exactement le modèle.
 */
export function csvJournal(ecritures: EcritureComptable[]): string {
  return ecritures
    .map((e) =>
      [
        e.date,
        e.journal,
        e.compte,
        e.libelle,
        montantCsv(e.debit),
        montantCsv(e.credit),
        '',
        e.piece,
        e.type,
        '',
        '',
        '',
        '',
      ].join(';'),
    )
    .join('\n')
    .concat(ecritures.length > 0 ? '\n' : '');
}

const compact = (iso: string): string => iso.slice(0, 10).replace(/-/g, '');

/** export20260723_du20260123au20260723.csv — le nom qu'Extrabat donne. */
export function nomFichierExport(genereLe: string, debut: string, fin: string): string {
  return `export${compact(genereLe)}_du${compact(debut)}au${compact(fin)}.csv`;
}

// Windows-1252 : ce que les logiciels comptables sous Windows lisent sans
// broncher. Les caractères hors table (rares dans un nom de client) deviennent « ? ».
const W1252_HAUT: Record<number, number> = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85, 0x2020: 0x86, 0x2021: 0x87,
  0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a, 0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e,
  0x2018: 0x91, 0x2019: 0x92, 0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
  0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b, 0x0153: 0x9c, 0x017e: 0x9e, 0x0178: 0x9f,
};

export function encoderWindows1252(texte: string): Uint8Array {
  const octets = new Uint8Array(texte.length);
  let n = 0;
  for (const car of texte) {
    const code = car.codePointAt(0) ?? 0x3f;
    if (code < 0x80 || (code >= 0xa0 && code <= 0xff)) octets[n] = code;
    else octets[n] = W1252_HAUT[code] ?? 0x3f;
    n += 1;
  }
  return octets.slice(0, n);
}

/** Un code de compte tel que le plan à 7 chiffres le connaît : 7061 -> 7061000. */
export function normaliserCompte(code: string): string {
  const chiffres = code.replace(/\D/g, '');
  return chiffres.length >= 7 ? chiffres.slice(0, 7) : chiffres.padEnd(7, '0');
}

/**
 * Cherche un compte dans le plan : exact d'abord, sinon le parent le plus
 * proche (7061000 absent -> 7060000 « Prestations de services »). Le
 * résultat dit lequel des deux a répondu ; l'écran ne confond pas les deux.
 */
export function chercherCompte(
  code: string,
  plan: ReadonlyMap<string, string>,
): { exact: boolean; code: string; libelle: string } | null {
  const c = normaliserCompte(code);
  if (!c || !/^\d{7}$/.test(c)) return null;
  const direct = plan.get(c);
  if (direct !== undefined) return { exact: true, code: c, libelle: direct };
  // Parent : on remplace le dernier chiffre non nul par 0, jusqu'à trouver.
  let parent = c;
  for (;;) {
    const i = parent.search(/0*$/) - 1;
    if (i < 1) return null;
    parent = `${parent.slice(0, i)}0${parent.slice(i + 1)}`;
    const l = plan.get(parent);
    if (l !== undefined) return { exact: false, code: parent, libelle: l };
  }
}

// ----- Listing détaillé des ventes ------------------------------------------
//
// Le journal ci-dessus parle au logiciel du comptable : des comptes, des débits
// et des crédits. Ce second fichier parle à l'entreprise — une ligne par ligne
// de facture, avec sa désignation, son taux et son montant, puis une ligne de
// total par facture. C'est le listing qu'on ouvre dans un tableur pour
// retrouver ce qui a été vendu, et à qui.

export interface LigneListingVentes {
  date: string;
  numero: string;
  /** « Facture » ou « Avoir ». */
  type: string;
  client: string;
  compteClient: string;
  /** Désignation de la ligne, ou « TOTAL » sur la ligne de récapitulation. */
  designation: string;
  /** Taux en pourcentage ('10' pour 10 %), vide si inconnu ou sur le total. */
  taux: string;
  montantHt: number;
  /** TVA de la ligne (calculée) ou de la facture (figée) sur la ligne de total. */
  montantTva: number | null;
  montantTtc: number | null;
  /** Vrai sur la ligne de récapitulation d'une facture. */
  total: boolean;
}

export const ENTETE_LISTING_VENTES = [
  'Date',
  'N° pièce',
  'Type',
  'Client',
  'Compte client',
  'Désignation',
  'Taux TVA',
  'Montant HT',
  'TVA',
  'Montant TTC',
] as const;

/**
 * Dresse le listing détaillé.
 *
 * Un avoir sort en NÉGATIF, comme partout ailleurs : le listing s'additionne
 * alors sans précaution particulière.
 *
 * Les montants de la ligne de TOTAL sont ceux que la facture a FIGÉS à
 * l'émission — jamais une somme des lignes recalculée ici. Les deux peuvent
 * différer d'un centime après arrondi, et c'est la facture qui fait foi.
 */
export function listingVentes(
  factures: FactureExport[],
  comptes: ComptesExport,
): LigneListingVentes[] {
  const out: LigneListingVentes[] = [];
  for (const f of factures) {
    const signe = f.kind === 'avoir' ? -1 : 1;
    const type = f.kind === 'avoir' ? 'Avoir' : 'Facture';
    const compte = compteClient(comptes, f.compteClient ?? null);
    const date = dateFrancaise(f.dateEmission);

    for (const l of f.lignes) {
      const ht = signe * l.totalHt;
      const tva = l.tauxTva === null ? null : Math.round(ht * l.tauxTva) / 100;
      out.push({
        date,
        numero: f.numero,
        type,
        client: f.client,
        compteClient: compte,
        designation: l.libelle,
        taux: l.tauxTva === null ? '' : String(l.tauxTva),
        montantHt: ht,
        montantTva: tva,
        montantTtc: tva === null ? null : Math.round((ht + tva) * 100) / 100,
        total: false,
      });
    }

    out.push({
      date,
      numero: f.numero,
      type,
      client: f.client,
      compteClient: compte,
      designation: 'TOTAL',
      taux: '',
      montantHt: signe * f.montantHt,
      montantTva: signe * f.montantTva,
      montantTtc: signe * Math.abs(f.montantTtc),
      total: true,
    });
  }
  return out;
}

/** Le listing en CSV : mêmes conventions que le journal, plus une ligne d'en-tête. */
export function csvListingVentes(lignes: LigneListingVentes[]): string {
  // Un point-virgule dans un nom de client ou une désignation casserait les
  // colonnes : il devient une virgule, qui se lit naturellement.
  const echapper = (s: string): string => s.replace(/;/g, ',');
  const corps = lignes.map((l) =>
    [
      l.date,
      l.numero,
      l.type,
      echapper(l.client),
      l.compteClient,
      echapper(l.designation),
      l.taux,
      montantCsv(l.montantHt),
      montantCsv(l.montantTva),
      montantCsv(l.montantTtc),
    ].join(';'),
  );
  return [ENTETE_LISTING_VENTES.join(';'), ...corps].join('\n').concat('\n');
}

/** ventes20260910_du20260901au20260910.csv */
export function nomFichierListing(genereLe: string, debut: string, fin: string): string {
  return `ventes${compact(genereLe)}_du${compact(debut)}au${compact(fin)}.csv`;
}
